import { getApiErrorCode } from './get-api-error-code';
import { getApiErrorContext } from './get-api-error-context';

/**
 * NPJB-800 — the backend's "still computing" answer.
 *
 * A cold intelligence read answers within about 10s with a 503 and this code. The work goes
 * on in the background, so the same request is retried until the result is ready.
 */
export const AUDIENCE_RESULT_COMPUTING = 'AUDIENCE_RESULT_COMPUTING';

/** Wait this long between tries when the answer names no `retryAfterSeconds`. */
export const COMPUTING_FALLBACK_DELAY_MS = 3_000;

/** Stop retrying after about this long, and show the error. */
export const COMPUTING_RETRY_BUDGET_MS = 3 * 60 * 1_000;

/**
 * How long the backend holds a cold read before it answers "still computing". TanStack's
 * retry hook gets no clock, so each try is counted as this hold plus its wait.
 */
const COMPUTING_HOLD_MS = 10_000;

/**
 * Whether a rejected read means "still computing". It reads both shapes `backend-request`
 * rejects with: the unwrapped `{ code, message, context }` and the raw `{ status, data }`.
 */
export const isComputingError = (err: unknown): boolean =>
  getApiErrorCode(err) === AUDIENCE_RESULT_COMPUTING;

/** The wait the backend asked for, in ms, or the 3s fallback. */
export const computingRetryDelayMs = (err: unknown): number => {
  const seconds = getApiErrorContext(err)?.retryAfterSeconds;
  return typeof seconds === 'number' && seconds > 0 ? seconds * 1_000 : COMPUTING_FALLBACK_DELAY_MS;
};

/**
 * Retry a "still computing" read while the ~3 min budget lasts; never retry anything else.
 * `failureCount` is the number of tries that have failed so far.
 */
export const shouldRetryComputing = (failureCount: number, err: unknown): boolean =>
  isComputingError(err) &&
  failureCount * (computingRetryDelayMs(err) + COMPUTING_HOLD_MS) < COMPUTING_RETRY_BUDGET_MS;

/**
 * The same retry for a plain call outside TanStack Query (e.g. the play domains read behind
 * Activate). Re-runs `request` while it answers "still computing", then returns or rethrows.
 */
export const withComputingRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  for (let failureCount = 0; ; failureCount++) {
    try {
      return await request();
    } catch (err) {
      if (!shouldRetryComputing(failureCount + 1, err)) throw err;
      await new Promise<void>((resolve) => {
        setTimeout(resolve, computingRetryDelayMs(err));
      });
    }
  }
};
